import React, { useEffect, useState } from "react"
import { Card, CardBody, Col, Row } from "reactstrap"
import { DatePicker } from "antd"
import Select from "react-select"
import { FaFilter } from "react-icons/fa"
import { customStyles } from "utils/SelectStyles"
import { months } from "utils/defaultpositions"
import { fetchData, FetchPostData } from "utils/Common_api_functions"
import Loader from "components/Loader/loader"
import SuccessRateChart from "../SuccessRateChart"
import SalesChart from "../SalesChart"
import BarChart from "../peakbarchart"
import OrderBarChart from "../OrderBarChart"
import secureLocalStorage from "react-secure-storage"
import dayjs from "dayjs"
import Swal from "sweetalert2"
import { getPreviousSaturday } from "utils/dateUtil"

const { RangePicker } = DatePicker

const currentYear = new Date().getFullYear()
const years = Array.from({ length: currentYear - 2020 }, (_, i) => ({
  label: `${2021 + i}`,
  value: 2021 + i,
})).reverse()

const HistoryPage = () => {
  const [loading, setLoading] = useState(false)
  const [historyData, setHistoryData] = useState({})
  const [dateRange, setDateRange] = useState([])
  const [selectedMonths, setSelectedMonths] = useState([])
  const [selectedYears, setSelectedYears] = useState([
    { label: `${currentYear}`, value: currentYear },
  ])
  const [maxDate, setMaxDate] = useState(null)
  const [currentWeek, setCurrentWeek] = useState([])
  const [sortOrder, setSortOrder] = useState("chronological")
  const [sortOrderGMP, setSortOrderGMP] = useState("chronological")
  const [sortOrderOEE, setSortOrderOEE] = useState("chronological")
  const [sortOrderNetMargin, setSortOrderNetMargin] = useState("chronological")

  const [ranges, setRanges] = useState([])

  useEffect(() => {
    fetchRanges()
  }, [])

  async function fetchRanges() {
    const APIdata = await fetchData(setLoading, "gauge/ranges")
    if (APIdata?.success) {
      setRanges(APIdata?.data)
    } else {
      Swal.fire({
        title: "Error Fetching Ranges",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  const fetchHistory = async (payload = {}) => {
    const data = await FetchPostData(
      setLoading,
      "sheet/dashboard/data",
      payload
    )
    if (data?.data?.success) setHistoryData(data.data)
    else setHistoryData({})
  }

  // Martin: "the automatic default should be the last week data was entered" —
  // on open, resolve the latest imported week (same source Home uses) and load
  // that week's year by default instead of assuming today's calendar year.
  useEffect(() => {
    const initDefaultWeek = async () => {
      const APIdata = await fetchData(setLoading, "sheet/latest/week")
      let latestYear = currentYear

      if (APIdata?.success) {
        const gmpDate = APIdata?.data?.gmpDate?.date
        const plDate = APIdata?.data?.plDate?.date
        const lowerStartDate =
          gmpDate && plDate
            ? new Date(gmpDate) >= new Date(plDate)
              ? gmpDate
              : plDate
            : gmpDate || plDate

        if (lowerStartDate) {
          const finalRange = [
            dayjs(getPreviousSaturday(lowerStartDate)),
            dayjs(lowerStartDate),
          ]
          setMaxDate(dayjs(lowerStartDate))
          setCurrentWeek(finalRange)
          latestYear = dayjs(lowerStartDate).year()
        }
      } else {
        const FinalRangeRaw = secureLocalStorage?.getItem("FINALRANGE")
        if (FinalRangeRaw?.[1]) {
          setMaxDate(dayjs(FinalRangeRaw[1]))
          setCurrentWeek([dayjs(FinalRangeRaw[0]), dayjs(FinalRangeRaw[1])])
          latestYear = dayjs(FinalRangeRaw[1]).year()
        }
      }

      setSelectedYears([{ label: String(latestYear), value: latestYear }])
      fetchHistory({ year: String(latestYear) })
    }

    initDefaultWeek()
  }, [])

  const handleFilter = () => {
    if (selectedYears?.length > 2) {
      Swal.fire({
        title: "Limit Reached",
        text: "You can select a maximum of 2 years.",
        icon: "warning",
        timer: 1500,
        showConfirmButton: false,
      })
      return
    }
    fetchHistory({
      startDate: dateRange?.[0]?.format("YYYY-MM-DD"),
      endDate: dateRange?.[1]?.format("YYYY-MM-DD"),
      month: selectedMonths?.map(i => i?.value)?.join(","),
      year: selectedYears?.map(i => i?.value)?.join(","),
    })
  }

  return (
    <div className="page-content">
      {loading && <Loader />}

      {/* Top bar */}
      <div
        style={{
          background: "#ffffff",
          borderRadius: "20px",
          border: "1px solid #e2e8f0",
          padding: "18px 24px",
          marginBottom: "24px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)",
          overflow: "visible",
        }}
      >
        <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
          {/* Left: title */}
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div>
              <h4
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#0f172a",
                  letterSpacing: "-0.3px",
                }}
              >
                History Charts
              </h4>
            </div>
            <div
              style={{ width: "1px", height: "36px", background: "#e2e8f0" }}
            />
            <p style={{ margin: 0, fontSize: "13px", color: "#64748b" }}>
              Year-over-year performance trends
            </p>
          </div>

          {/* Right: filters */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            <RangePicker
              value={dateRange}
              placeholder={["Start (Sat)", "End (Fri)"]}
              onChange={setDateRange}
              format="DD-MM-YYYY"
              disabledDate={current =>
                maxDate && current > maxDate.endOf("day")
              }
            />
            <Select
              isMulti
              options={months}
              value={selectedMonths}
              onChange={v => {
                setSelectedMonths(v)
                // Reset ALL sort orders when month filter is cleared
                if (!v || v.length === 0) {
                  setSortOrder("chronological")
                  setSortOrderGMP("chronological")
                  setSortOrderOEE("chronological")
                  setSortOrderNetMargin("chronological")
                }
              }}
              styles={customStyles}
              placeholder="Month"
              closeMenuOnSelect={false}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            <Select
              isMulti
              options={years}
              value={selectedYears}
              onChange={v => {
                if (v?.length > 2) {
                  Swal.fire({
                    title: "Limit Reached",
                    text: "Max 2 years.",
                    icon: "warning",
                    timer: 1500,
                    showConfirmButton: false,
                  })
                  return
                }
                setSelectedYears(v)
                // Reset sort order when year filter is cleared
                if (!v || v.length === 0) setSortOrder("chronological")
              }}
              styles={customStyles}
              placeholder="Year"
              closeMenuOnSelect={false}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            <button
              onClick={handleFilter}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "5px",
                background: "#3b82f6",
                border: "none",
                borderRadius: "8px",
                padding: "0 14px",
                height: "36px",
                color: "#fff",
                fontSize: "12px",
                fontWeight: 700,
                cursor: "pointer",
                letterSpacing: "0.3px",
              }}
            >
              <FaFilter size={10} />
            </button>
          </div>
        </div>
      </div>

      {/* Charts */}
      <Row>
        <Col xl={12}>
          <Card className="bg-white shadow rounded-4">
            <CardBody>
              <SuccessRateChart
                data={historyData?.salesRevenue}
                previousData={historyData?.salesRevenuePreviousYear}
                maxValue={ranges?.[0]?.max}
                showWeekCard
                sortOrder={sortOrder}
                onSortOrderChange={setSortOrder}
              />
            </CardBody>
          </Card>
        </Col>

        <Col xl={12}>
          <Card className="bg-white shadow rounded-4">
            <CardBody>
              <BarChart
                data={historyData?.gmp}
                previousData={historyData?.gmpPreviousYear}
                closeOut={historyData?.closeOut}
                closeOutPreviousYear={historyData?.closeOutPreviousYear}
                sortOrder={sortOrderGMP}
                onSortOrderChange={setSortOrderGMP}
              />
            </CardBody>
          </Card>
        </Col>

        <Col xl={12}>
          <Card className="bg-white shadow rounded-4">
            <CardBody>
              <SalesChart
                data={historyData?.oee}
                previousData={historyData?.oeePreviousYear}
                sortOrder={sortOrderOEE}
                onSortOrderChange={setSortOrderOEE}
              />
            </CardBody>
          </Card>
        </Col>

        <Col xl={12}>
          <Card className="bg-white shadow rounded-4">
            <CardBody>
              <OrderBarChart
                data={historyData?.finalNetMarginFirstYear}
                previousData={historyData?.finalNetMarginSecondYear}
                maxValue={ranges?.[4]?.max}
                sortOrder={sortOrderNetMargin}
                onSortOrderChange={setSortOrderNetMargin}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default HistoryPage
